'use client';

import { getAuth } from 'firebase/auth';
import { firebaseApp } from '@/utils/firebase';
import { useSiteContext } from '@/context/SiteContext';
import type { VerifyIdTokenResult } from '@/utils/sso';
import { useOverlayContext } from '@/context/OverlayContext';
import { useGaContext } from '@/context/GoogleAnalyticsContext';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
	LoginProvider,
	type LoginType,
	useLoginContext,
} from '@/context/LoginContext';
import { FollowLoginOverlay } from '@/components/raven/global/FollowButton/FollowLoginOverlay';
import { FollowButtonComponent } from '@/components/raven/global/FollowButton/FollowButtonComponent';
import {
	FollowingState,
	type FollowButtonProps,
} from '@/components/raven/global/FollowButton/types';
import {
	handleClick,
	fetchFollowInfo,
	handleAfterLogin,
} from '@/components/raven/global/FollowButton/util';

export const FollowButtonWrapper: React.FC<FollowButtonProps> = ({
	className,
	containerClassName,
	followableId,
	followType,
	labelProps,
	variant,
}) => {
	const { afterCompleteLoginFnRef, rgisCookie } = useLoginContext();
	const { toaster } = useSiteContext();
	const { sendGaEvent } = useGaContext('FollowButton');
	const { setToast } = toaster;
	const { openVariant } = useOverlayContext();

	// FollowingState.Unknown set by default to prevent hydration errors.
	const [followingState, setFollowingState] = useState(FollowingState.Unknown);
	const [isFetchingAfterLogin, setIsFetchingAfterLogin] = useState(false);
	const [productItemId, setProductItemId] = useState<null | string>(null);

	useEffect(() => {
		fetchFollowInfo({
			followableId,
			followingState,
			followType,
			isFetchingAfterLogin,
			rgisCookie,
			setFollowingState,
			setProductItemId,
		});
	}, [
		rgisCookie,
		followableId,
		followType,
		isFetchingAfterLogin,
		followingState,
		setFollowingState,
		setProductItemId,
	]);

	// Set up the after login callback
	useEffect(() => {
		afterCompleteLoginFnRef.current = async (
			tokenResult: VerifyIdTokenResult,
			loginType: LoginType,
		) => {
			await handleAfterLogin(tokenResult, loginType, {
				followableId,
				followType,
				sendGaEvent,
				setFollowingState,
				setIsFetchingAfterLogin,
				setProductItemId,
				setToast,
			});
		};
	}, [
		afterCompleteLoginFnRef,
		followType,
		followableId,
		sendGaEvent,
		setToast,
	]);

	const onButtonClick = useCallback(() => {
		handleClick({
			followableId,
			followingState,
			followType,
			openVariant,
			productItemId,
			rgisCookie,
			sendGaEvent,
			setFollowingState,
			setToast,
		});
	}, [
		followableId,
		followType,
		followingState,
		productItemId,
		rgisCookie,
		sendGaEvent,
		setFollowingState,
		setToast,
		openVariant,
	]);

	return (
		<>
			<FollowButtonComponent
				className={className}
				containerClassName={containerClassName}
				followingState={followingState}
				id={`follow-btn-${followableId}`}
				labelProps={labelProps}
				onClick={onButtonClick}
				variant={variant}
			/>
			<FollowLoginOverlay
				followableId={followableId}
				followingState={followingState}
				followType={followType}
				isFetchingAfterLogin={isFetchingAfterLogin}
			/>
		</>
	);
};

export const FollowButton: React.FC<FollowButtonProps> = (props) => {
	const { firebase } = useSiteContext();

	const auth = useMemo(() => {
		return getAuth(firebaseApp(firebase.config));
	}, [firebase]);

	return (
		<LoginProvider auth={auth}>
			<FollowButtonWrapper {...props} />
		</LoginProvider>
	);
};
